export const COLLECTION_TYPES = {
    ANNOTATIONS: 'annotations',
    ARCHIVED_ANNOTATIONS: 'archived-annotations',
    ASSIGNMENTS: 'assignments',
    AUDIT: 'audit',
    BULK_CODES: 'bulk-codes',
    EMAIL_TEMPLATES: 'mail-templates',
    ENTITIES_ARCHIVE: 'entities-archive',
    ENTITY_TYPE_FIELDS: 'entity-type-fields',
    ENTITY_TYPES: 'entity-types',
    FILE_CACHE_MARKERS: 'file-cache-markers',
    MAIL: 'mail',
    PROVIDERS: 'providers',
    REMINDERS: 'reminders',
    SUBMISSIONS: 'submissions',
    SUBSCRIPTIONS: 'subscriptions',
    TENANTS: 'tenants',
    TRANSLATION_PAGE_DICTIONARY: 'translation-page-dictionary',
    TRANSLATION_STRING_DICTIONARY: 'translation-string-dictionary',
    MARKETING_MATERIALS: 'marketing-materials',
    USERS: 'users',
    TAGS: 'tags',
    ROLES: 'roles',
    ACTIONS: 'actions',
    CONTENT_ACTIONS: 'content-actions',
    DISTRICTS: 'districts',
    DISTRICT_GROUPS: 'district-groups',
    SCHOOLS: 'schools',
    SCHOOL_GROUPS: 'school-groups',
    CLASSES: 'classes',
    CLASS_GROUPS: 'class-groups',
    COURSES: 'courses',
    COLLECTIONS: 'collections',
    RESOURCES: 'resources',
    NOTIFICATIONS: 'notifications',
    LESSON_PLANNERS: 'lesson-planners',
    READ_ONLY_TITLES: 'read-only-titles',
    READ_ONLY_RESOURCE_HUB: 'read-only-resource-hub',
    READ_ONLY_LESSON_PLANNER_RESOURCES: 'read-only-lesson-planner-resources',
    RSS_FEEDS: 'rss-feeds',
    RSS_WIDGET_CACHES: 'rss-widget-caches',
    STATUS_BANNER: 'status-banner',
    BOOK_STAGING_JOBS: 'book-staging-jobs',
    BOOK_UPLOADS: 'book-uploads',
    BOOK_GENERATION_JOBS: 'book-generation-jobs',
    LEGACY_UPLOAD_HISTORY: 'legacy-upload-history',
};

// Collections whose entities are cached after read.
export const DEFAULT_CACHABLE_COLLECTIONS = [
    COLLECTION_TYPES.COLLECTIONS,
    COLLECTION_TYPES.RESOURCES,
];

// Collections whose documents can be soft-deleted from the data browser.
// Archiving stamps `archived: true` on the document rather than removing it
export const ARCHIVABLE_COLLECTIONS = new Set([
    COLLECTION_TYPES.USERS,
    COLLECTION_TYPES.CLASSES,
    COLLECTION_TYPES.DISTRICTS,
    COLLECTION_TYPES.SCHOOLS,
    COLLECTION_TYPES.SUBSCRIPTIONS,
    COLLECTION_TYPES.COURSES,
    COLLECTION_TYPES.ASSIGNMENTS,
]);

// Collections whose documents may carry `archived: true` without every doc
// having the field (the school year rollover archives a repeating student's
// annotations in place). Readers drop archived docs from results, but no
// `archived == false` filter is added to the query: that would also drop the
// docs that predate the field.
export const ARCHIVE_FLAGGED_COLLECTIONS = new Set([
    COLLECTION_TYPES.ANNOTATIONS,
]);

// Collections that live in the shared `content-db` Firestore. Used by
// each app's dbFor() router to pick the right Firestore instance.
export const CONTENT_COLLECTIONS = new Set([
    COLLECTION_TYPES.RESOURCES,
    COLLECTION_TYPES.COLLECTIONS,
    COLLECTION_TYPES.COURSES,
    COLLECTION_TYPES.ROLES,
    COLLECTION_TYPES.ACTIONS,
    COLLECTION_TYPES.CONTENT_ACTIONS,
    COLLECTION_TYPES.ENTITY_TYPES,
    COLLECTION_TYPES.ENTITY_TYPE_FIELDS,
    COLLECTION_TYPES.TAGS,
    COLLECTION_TYPES.STATUS_BANNER,
    COLLECTION_TYPES.RSS_FEEDS,
    // rss-widget-caches is the derived cache for RSS_FEEDS; kept on content-db
    // so it sits alongside the source feeds and is shareable across apps.
    COLLECTION_TYPES.RSS_WIDGET_CACHES,
    COLLECTION_TYPES.MARKETING_MATERIALS,
    COLLECTION_TYPES.READ_ONLY_TITLES,
    // read-only-resource-hub is the resource-hub projection of content; it
    // sits alongside read-only-titles in content-db.
    COLLECTION_TYPES.READ_ONLY_RESOURCE_HUB,
    // read-only-lesson-planner-resources stays in `(default)` (Education) —
    // lesson planners are classroom data, not shared content.
    COLLECTION_TYPES.TRANSLATION_PAGE_DICTIONARY,
    COLLECTION_TYPES.TRANSLATION_STRING_DICTIONARY,
    COLLECTION_TYPES.BOOK_STAGING_JOBS,
    COLLECTION_TYPES.BOOK_UPLOADS,
    COLLECTION_TYPES.BOOK_GENERATION_JOBS,
    // legacy-upload-history audits content imports; lives alongside the
    // collections/resources it writes so the whole import runs in one DB.
    COLLECTION_TYPES.LEGACY_UPLOAD_HISTORY,
]);

export function isContentCollection(collectionName) {
    return CONTENT_COLLECTIONS.has(collectionName);
}
